// The starting chart of accounts a company is provisioned with (technical design §3.2 step 4):
// a template per country and trade. Companies add, rename and deactivate accounts; the roles say
// which account each posting rule uses. Codes are a convention, not meaning.

import type { AccountRole, AccountType } from './ledger.ts';

/** What reports group accounts by (balance sheet sections, the cash flow statement). */
export type AccountSubtype =
  | 'bank'
  | 'current_asset'
  | 'inventory'
  | 'fixed_asset'
  | 'current_liability'
  /** A company credit card: owed, and reconciled from its statement like a bank (BL-012). */
  | 'credit_card'
  | 'long_term_liability'
  | 'equity'
  | 'income'
  | 'cost_of_sales'
  | 'expense'
  | 'other_income';

export interface ChartAccount {
  code: string;
  name: string;
  type: AccountType;
  subtype: AccountSubtype;
  roles?: readonly { role: AccountRole; key?: string }[];
}

export const SUBTYPE_TYPE: Record<AccountSubtype, AccountType> = {
  bank: 'asset',
  current_asset: 'asset',
  inventory: 'asset',
  fixed_asset: 'asset',
  current_liability: 'liability',
  credit_card: 'liability',
  long_term_liability: 'liability',
  equity: 'equity',
  income: 'income',
  other_income: 'income',
  cost_of_sales: 'expense',
  expense: 'expense',
};

const a = (
  code: string,
  name: string,
  subtype: AccountSubtype,
  roles?: ChartAccount['roles'],
): ChartAccount => ({ code, name, type: SUBTYPE_TYPE[subtype], subtype, ...(roles ? { roles } : {}) });

/** Canada, home-service trades (plumbing, heating, electrical). GST/HST and PST are tax component codes. */
const CA_SERVICE_TRADE: readonly ChartAccount[] = [
  a('1000', 'Bank: operating', 'bank'),
  a('1010', 'Undeposited funds', 'current_asset', [{ role: 'undeposited_funds' }]),
  a('1100', 'Accounts receivable', 'current_asset', [{ role: 'accounts_receivable' }]),
  a('1150', 'Due from financing lender', 'current_asset', [{ role: 'financing_receivable' }]),
  a('1200', 'Inventory', 'inventory', [{ role: 'inventory' }]),
  a('1250', 'Returns awaiting credit', 'current_asset', [{ role: 'vendor_returns' }]),
  a('1500', 'Vehicles and equipment', 'fixed_asset'),
  a('2000', 'Accounts payable', 'current_liability', [{ role: 'accounts_payable' }]),
  a('2010', 'Goods received, not billed', 'current_liability', [{ role: 'goods_received_accrual' }]),
  a('2100', 'GST/HST payable', 'current_liability', [
    { role: 'tax_payable', key: 'GST' },
    { role: 'tax_payable', key: 'HST' },
  ]),
  a('2110', 'PST payable', 'current_liability', [{ role: 'tax_payable', key: 'PST' }]),
  a('2150', 'Customer deposits', 'current_liability', [{ role: 'customer_credits', key: 'deposit' }]),
  a('2200', 'Payroll liabilities', 'current_liability', [{ role: 'payroll_liabilities' }]),
  a('2300', 'Deferred revenue: memberships', 'current_liability', [{ role: 'deferred_revenue' }]),
  a('2400', 'Customer credits and deposits', 'current_liability', [{ role: 'customer_credits' }]),
  a('3000', 'Owner equity', 'equity'),
  a('3100', 'Retained earnings', 'equity', [{ role: 'retained_earnings' }]),
  a('3900', 'Opening balance equity', 'equity', [{ role: 'opening_balance_equity' }]),
  a('4000', 'Service income', 'income', [{ role: 'income', key: '' }]),
  a('4100', 'Membership income', 'income', [{ role: 'membership_income' }]),
  a('5000', 'Cost of goods: materials', 'cost_of_sales', [{ role: 'cogs' }]),
  a('5050', 'Purchase price variance', 'cost_of_sales', [{ role: 'purchase_price_variance' }]),
  a('5060', 'Inventory adjustments', 'cost_of_sales', [{ role: 'inventory_adjustment' }]),
  a('5100', 'Field wages', 'cost_of_sales', [{ role: 'wages_field' }]),
  a('5110', 'Commissions', 'cost_of_sales', [{ role: 'commissions' }]),
  a('5120', 'Employer payroll costs', 'expense', [{ role: 'employer_costs' }]),
  a('6000', 'Vehicle fuel', 'expense'),
  a('6010', 'Card processing fees', 'expense', [{ role: 'processor_fees' }, { role: 'dispute_fees' }]),
  a('6015', 'Consumer financing fees', 'expense', [{ role: 'financing_fees' }]),
  a('6020', 'Rent', 'expense'),
  a('6030', 'Utilities', 'expense'),
  a('6040', 'Software and phones', 'expense'),
  a('6050', 'Shop and office supplies', 'expense'),
  // the bank's own monthly fee and interest, as they come on the bank feed (QA-810)
  a('6060', 'Bank charges and interest', 'expense'),
  a('6070', 'Bad debts', 'expense', [{ role: 'bad_debts' }]),
  a('6080', 'Rounding', 'expense', [{ role: 'rounding' }]),
  a('6100', 'Office wages', 'expense', [{ role: 'wages_office' }]),
];

/**
 * Sales tax payable accounts for the codes the template does not already carry (GST/HST and PST do):
 * Québec's QST and Manitoba's RST. Installed with the chart when the company's tax zones use them,
 * so the first invoice in those provinces posts (QA-262).
 */
export const PROVINCIAL_TAX_ACCOUNTS: Readonly<Record<string, ChartAccount>> = {
  QST: a('2120', 'QST payable', 'current_liability', [{ role: 'tax_payable', key: 'QST' }]),
  RST: a('2115', 'RST payable', 'current_liability', [{ role: 'tax_payable', key: 'RST' }]),
};

export const CHART_TEMPLATES: Record<string, readonly ChartAccount[]> = {
  'CA:service_trade': CA_SERVICE_TRADE,
};

export function chartTemplate(country: string, trade = 'service_trade'): readonly ChartAccount[] | undefined {
  return CHART_TEMPLATES[`${country}:${trade}`];
}
